import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const Dashboard = ({ user: propsUser }) => {
  const [user, setUser] = useState(propsUser);
  const [scans, setScans] = useState([]);
  const [filteredScans, setFilteredScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');
  const [filterJurisdiction, setFilterJurisdiction] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, scansRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/scans`, { withCredentials: true })
        ]);
        setUser(userRes.data);
        setScans(scansRes.data);
        setFilteredScans(scansRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (!propsUser) {
      fetchData();
    } else {
      axios.get(`${API}/scans`, { withCredentials: true })
        .then(res => {
          setScans(res.data);
          setFilteredScans(res.data);
        })
        .catch(err => toast.error('Failed to load scans'))
        .finally(() => setLoading(false));
    }
  }, [propsUser]);

  // Filter scans based on search and filters
  useEffect(() => {
    let filtered = [...scans];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(scan => 
        (scan.ad_text && scan.ad_text.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (scan.landing_page_url && scan.landing_page_url.toLowerCase().includes(searchQuery.toLowerCase())) ||
        scan.scan_id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Risk level filter
    if (filterRiskLevel !== 'all') {
      filtered = filtered.filter(scan => scan.risk_level.toLowerCase() === filterRiskLevel);
    }

    // Jurisdiction filter
    if (filterJurisdiction !== 'all') {
      filtered = filtered.filter(scan => scan.target_jurisdiction === filterJurisdiction);
    }

    // Platform filter
    if (filterPlatform !== 'all') {
      filtered = filtered.filter(scan => scan.target_platform === filterPlatform);
    }

    setFilteredScans(filtered);
  }, [searchQuery, filterRiskLevel, filterJurisdiction, filterPlatform, scans]);

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'text-risk-critical';
      case 'high': return 'text-risk-high';
      case 'moderate': return 'text-risk-moderate';
      case 'low': return 'text-risk-low';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskBgColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'bg-risk-critical/10 border-risk-critical';
      case 'high': return 'bg-risk-high/10 border-risk-high';
      case 'moderate': return 'bg-risk-moderate/10 border-risk-moderate';
      case 'low': return 'bg-risk-low/10 border-risk-low';
      default: return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name}</p>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card data-testid="plan-card">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Current Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{user?.plan_display_name || user?.plan?.replace('starter', 'Beta Starter').replace('free', 'Free').replace('pro', 'Pro').replace('agency', 'Agency') || 'Free'}</div>
                {user?.subscription_status === 'active' && (
                  <p className="text-xs text-green-600 mt-1">Active Subscription</p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="scans-used-card">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {user?.plan === 'free' ? 'Scans Used (Lifetime)' : 'Scans Used (This Month)'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{user?.scans_used || 0} / {user?.scan_limit || 3}</div>
                {user?.scans_used >= user?.scan_limit && (
                  <p className="text-xs text-red-600 mt-1">Limit reached</p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="total-scans-card">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Scans (Lifetime)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{user?.total_scans || scans.length || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold mb-4">Quick Actions</h2>
            <div className="flex gap-4">
              <Link to="/scanner" data-testid="quick-scan-btn">
                <Button size="lg">
                  <Shield className="mr-2 h-5 w-5" />
                  New Compliance Scan
                </Button>
              </Link>
              <Link to="/pricing" data-testid="upgrade-plan-btn">
                <Button size="lg" variant="outline">
                  <TrendingUp className="mr-2 h-5 w-5" />
                  Upgrade Plan
                </Button>
              </Link>
            </div>
          </div>

          {/* Recent Scans */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold" data-testid="recent-scans-title">Recent Scans</h2>
              <div className="text-sm text-muted-foreground">
                Showing {filteredScans.length} of {scans.length} scans
              </div>
            </div>

            {/* Search and Filters */}
            {scans.length > 0 && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div className="md:col-span-2">
                      <Label htmlFor="search">Search</Label>
                      <Input
                        id="search"
                        type="text"
                        placeholder="Search by ad text, URL, or scan ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="search-scans-input"
                      />
                    </div>

                    {/* Risk Level Filter */}
                    <div>
                      <Label htmlFor="riskFilter">Risk Level</Label>
                      <select
                        id="riskFilter"
                        value={filterRiskLevel}
                        onChange={(e) => setFilterRiskLevel(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="filter-risk-level"
                      >
                        <option value="all">All Levels</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="moderate">Moderate</option>
                        <option value="low">Low</option>
                      </select>
                    </div>

                    {/* Jurisdiction Filter */}
                    <div>
                      <Label htmlFor="jurisdictionFilter">Jurisdiction</Label>
                      <select
                        id="jurisdictionFilter"
                        value={filterJurisdiction}
                        onChange={(e) => setFilterJurisdiction(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="filter-jurisdiction"
                      >
                        <option value="all">All Jurisdictions</option>
                        <option value="Canada">Canada</option>
                        <option value="US">United States</option>
                        <option value="EU">European Union</option>
                        <option value="India">India</option>
                      </select>
                    </div>
                  </div>

                  {/* Platform Filter */}
                  <div className="mt-4">
                    <Label htmlFor="platformFilter">Platform</Label>
                    <select
                      id="platformFilter"
                      value={filterPlatform}
                      onChange={(e) => setFilterPlatform(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-w-xs"
                      data-testid="filter-platform"
                    >
                      <option value="all">All Platforms</option>
                      <option value="Meta">Meta</option>
                      <option value="Google">Google</option>
                      <option value="TikTok">TikTok</option>
                      <option value="Amazon">Amazon</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="YouTube">YouTube</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {scans.length === 0 ? (
              <Card data-testid="no-scans-card">
                <CardContent className="py-12 text-center">
                  <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">No scans yet</h3>
                  <p className="text-muted-foreground mb-6">Start your first compliance scan to detect regulatory risks.</p>
                  <Link to="/scanner">
                    <Button data-testid="first-scan-btn">Create First Scan</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredScans.map((scan) => (
                  <Card key={scan.scan_id} className="hover:shadow-md transition-shadow" data-testid={`scan-card-${scan.scan_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRiskBgColor(scan.risk_level)}`}>
                              <AlertCircle className={`mr-1 h-4 w-4 ${getRiskColor(scan.risk_level)}`} />
                              <span className={getRiskColor(scan.risk_level)}>{scan.risk_level}</span>
                            </span>
                            <span className="text-sm text-muted-foreground">
                              Score: {scan.risk_score}/100
                            </span>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                            {scan.ad_text || scan.landing_page_url || 'Image/Funnel scan'}
                          </p>
                          
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="font-mono">{scan.target_jurisdiction}</span>
                            <span>•</span>
                            <span>{scan.target_platform}</span>
                            <span>•</span>
                            <span className="flex items-center">
                              <Clock className="mr-1 h-3 w-3" />
                              {new Date(scan.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <Link to={`/scan/${scan.scan_id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-scan-${scan.scan_id}`}>View Report</Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
